// Buszyklus-Ansicht: Die CPU legt Adresse, Daten und Steuersignale an (Schalter antippen),
// „Takt“ schickt die Bits über die Leitungen, Speicher bzw. E/A-Werk reagieren.
// Aufbau wie auf der Folie „Speicherzugriff ganz konkret: Die einzelnen Leitungen“.

import { reducedMotion } from "./confetti.ts";
import { ICON } from "./icons.ts";
import { MAX_TAKTE, checkZyklus, istRechnen, start, takt } from "./model.ts";
import type { Bit, Ereignis, Op, Schritt, Signal, ZyklusResult, ZyklusTask, Zustand } from "./model.ts";
import { store } from "./store.ts";

const W = 1000;
const GAP = 30;                  // Abstand zweier Leitungen
const X_CPU = 250, X_MEM = 720;  // Leitungen laufen von der CPU-Kante zur Speicher-Kante
const X_TOGGLE = 186;            // Schalter am rechten Rand der CPU
const X_REG = 74, REG_W = 26;    // Datenregister: eine Zelle je Datenleitung, direkt neben D
const X_REG_BTN = 106, REG_BTN_W = 74; // Knopf „Register → Datenbus“ zwischen Register und Schaltern
const OP_W = 60, OP_H = 24;            // Rechenknöpfe +1 / ×2 unter dem Register
const OP_TEXT: Record<Op, string> = { inc: "+1", shl: "×2" };
const TAKT_W = 100, TAKT_H = 34;       // zweiter „Takt“-Knopf direkt unter den Schaltern
const EA_X0 = 440, EA_X1 = 700;  // E/A-Werk unterhalb der Leitungen
const FLY_MS = 850;

type Gruppe = "A" | "D" | "S";
interface Line { key: string; name: string; gruppe: Gruppe; y: number }
interface Flight { pts: [number, number][]; bit: string }

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
// „RW“ mit Strich über dem W: geschrieben wird bei 0 (W ist low-aktiv), gelesen bei 1
const RW_SVG = `R<tspan class="overline">W</tspan>`;
const RW_HTML = `R<span class="overline-html">W</span>`;
const rwHtml = (html: string) => html.replace(/\bRW\b/g, RW_HTML);
const cls = (b: Bit | string) => (b === 1 || b === "1" ? "hi" : "lo");
const wait = (ms: number) => new Promise(r => window.setTimeout(r, ms));

/** Text für das Protokoll: Was hat dieser Takt bewirkt? */
export function beschreibe(e: Ereignis): string {
  switch (e.typ) {
    case "niemand": return "Kein Chip Select gesetzt -- niemand fühlt sich angesprochen.";
    case "nur_ausgabe": return "Das E/A-Werk kann nur ausgeben, nicht gelesen werden.";
    case "lesen": return `Speicherzelle ${e.adresse} liefert ${e.wert} → Register`;
    case "schreiben":
      return e.ziele.map(z => (z === "speicher" ? `Speicherzelle ${e.adresse} ← ${e.wert}` : `Ausgabe zeigt ${e.wert}`)).join(" · ");
    case "rechnen":
      return `Register ${e.vorher} ${e.op === "inc" ? "+ 1" : "× 2"} → ${e.wert}`
        + (e.ueberlauf ? " (Überlauf: das Ergebnis passt nicht mehr in das Register, die vorderste 1 geht verloren)" : "");
  }
}

export class ZyklusView {
  private a: Bit[];
  private d: Bit[];
  private rw: Bit = 1;
  private csS: Bit = 0;
  private csE: Bit = 0;
  private takte: Schritt[];
  private ereignisse: Ereignis[] = [];
  private zustand: Zustand;
  private busWert: string | null = null; // vom Speicher getriebener Datenbus direkt nach dem Lesen
  private busy = false;
  private result: ZyklusResult | null = null;
  private solvedShown = false;
  private hintsShown: number;
  private flash = new Set<string>();     // Elemente, die beim nächsten Rendern kurz aufleuchten
  private note = "";
  private popStars = false;

  private readonly lines: Line[];
  private readonly H: number;
  private readonly memTop: number;
  private readonly memBottom: number;
  private readonly eaTop: number;
  private readonly svg: SVGSVGElement;
  private readonly side: HTMLElement;
  private readonly flyLayer: SVGGElement;
  private readonly root: HTMLElement;
  private readonly task: ZyklusTask;
  private readonly next: { id: string; titel: string } | null;

  constructor(root: HTMLElement, task: ZyklusTask, next: { id: string; titel: string } | null = null) {
    this.root = root;
    this.task = task;
    this.next = next;
    this.a = Array(task.abits).fill(0);
    this.d = Array(task.bits).fill(0);
    this.hintsShown = store.hints(task.id);

    // Leitungen von oben nach unten: Adressbus, Datenbus, Steuerbus (mit Lücke dazwischen)
    let y = 90;
    const lines: Line[] = [];
    const add = (key: string, name: string, gruppe: Gruppe) => { lines.push({ key, name, gruppe, y }); y += GAP; };
    for (let i = 0; i < task.abits; i++) add(`A${i}`, `A${task.abits - 1 - i}`, "A");
    y += GAP;
    for (let i = 0; i < task.bits; i++) add(`D${i}`, `D${task.bits - 1 - i}`, "D");
    y += GAP;
    add("RW", "RW", "S");
    add("CS_S", "CS Speicher", "S");
    if (task.ea) add("CS_E", "CS E/A", "S");
    this.lines = lines;
    const last = lines[lines.length - 1].y;
    this.memTop = 40;
    this.memBottom = this.line("CS_S").y + 24;
    this.eaTop = last + 50;
    this.H = last + 170;

    this.takte = store.takte(task.id).slice(0, MAX_TAKTE);
    this.zustand = start(task);
    this.replay();
    const lastSig = [...this.takte].reverse().find(s => !istRechnen(s)) as Signal | undefined;
    if (lastSig) this.setSignal(lastSig);

    root.innerHTML = `
      <section class="task-head">
        <a class="back" href="#">← Aufgaben</a>
        <h1>${esc(task.titel)}</h1>
        <p class="barometer" hidden></p>
        ${task.text ? `<p>${esc(task.text)}</p>` : ""}
      </section>
      <div class="workbench zyklus">
        <div class="canvas-wrap">
          <svg class="canvas" viewBox="0 0 ${W} ${this.H}" role="application"
               aria-label="CPU, Bus und Speicher"><g class="layer"></g><g class="fly"></g></svg>
          <div class="toolbar">
            <button data-act="undo" title="Letzten Takt zurücknehmen">${ICON.undo}<span>Rückgängig</span></button>
            <button data-act="clear" title="Speicher zurücksetzen, alle Takte löschen">Neu</button>
            <span class="spacer"></span>
            <button data-act="hint" class="secondary"></button>
            <button data-act="check">Prüfen</button>
            <button data-act="takt" class="primary">${ICON.play}<span>Takt</span></button>
          </div>
        </div>
        <aside class="side"></aside>
      </div>`;
    this.svg = root.querySelector("svg.canvas")!;
    this.flyLayer = this.svg.querySelector("g.fly")!;
    this.side = root.querySelector(".side")!;

    this.svg.addEventListener("click", e => {
      const target = e.target as Element;
      const act = target.closest("[data-act]")?.getAttribute("data-act");
      if (act === "reg-to-bus") {
        void this.registerToBus();
        return;
      }
      if (act === "takt") {
        this.action("takt");
        return;
      }
      if (act === "inc" || act === "shl") {
        this.rechnen(act);
        return;
      }
      const key = target.closest<SVGGElement>("[data-line]")?.dataset.line;
      if (key) this.toggle(key);
    });
    root.querySelector(".toolbar")!.addEventListener("click", e => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act) this.action(act);
    });
    this.render();
  }

  destroy(): void {
    this.busy = true; // laufende Animation bricht beim nächsten Schritt ab
  }

  // ---------------------------------------------------------------- Zustand

  private line(key: string): Line {
    return this.lines.find(l => l.key === key)!;
  }

  private signal(): Signal {
    const s: Signal = { A: this.a.join(""), RW: this.rw, CS_S: this.csS, CS_E: this.task.ea ? this.csE : 0 };
    if (this.rw === 0) s.D = this.d.join("");
    return s;
  }

  private setSignal(s: Signal): void {
    this.a = [...s.A].map(Number) as Bit[];
    if (s.D) this.d = [...s.D].map(Number) as Bit[];
    this.rw = s.RW;
    this.csS = s.CS_S ?? 0;
    this.csE = s.CS_E ?? 0;
  }

  private replay(): void {
    let z = start(this.task);
    const ev: Ereignis[] = [];
    const ok: Schritt[] = [];
    for (const s of this.takte) {
      const step = takt(this.task, z, s);
      if (!step) break; // gespeicherte Takte aus einer älteren Aufgabenversion
      z = step[0];
      ev.push(step[1]);
      ok.push(s);
    }
    this.takte = ok;
    this.zustand = z;
    this.ereignisse = ev;
  }

  private toggle(key: string): void {
    if (this.busy) return;
    const flip = (b: Bit): Bit => (b ? 0 : 1);
    const idx = Number(key.slice(1));
    if (key === "RW") this.rw = flip(this.rw);
    else if (key === "CS_S") this.csS = flip(this.csS);
    else if (key === "CS_E") this.csE = flip(this.csE);
    else if (key[0] === "A") this.a[idx] = flip(this.a[idx]);
    else if (key[0] === "D" && this.task.daten_aus_register) {
      this.note = "In dieser Aufgabe sind die Datenschalter gesperrt: Geschrieben wird nur, was im Register liegt. "
        + "Erst lesen, dann „Register → Datenbus“.";
      this.render();
      return;
    } else if (key[0] === "D" && this.rw === 0) this.d[idx] = flip(this.d[idx]);
    else return;
    this.busWert = null;
    this.note = "";
    this.render();
  }

  /** Die CPU legt den Registerinhalt an die Datenleitungen (nur beim Schreiben treibt sie D). */
  private async registerToBus(): Promise<void> {
    const reg = this.zustand.register;
    if (this.busy || reg === null) return;
    if (this.rw === 1) {
      this.note = "Beim Lesen legt der Speicher die Daten an. Stellen Sie erst RW = 0 (schreiben) ein.";
      this.render();
      return;
    }
    this.busy = true;
    this.note = "";
    this.render();
    if (!reducedMotion()) {
      await this.fly([...reg].map((bit, i) => {
        const y = this.line(`D${i}`).y;
        return { pts: [[X_REG + REG_W / 2, y], [X_TOGGLE + 26, y]], bit };
      }));
      if (!this.root.isConnected) return;
    }
    this.d = [...reg].map(Number) as Bit[];
    this.busy = false;
    this.render();
  }

  /** Rechnung im Register (+1 oder ×2): kein Bus, aber ein eigener Schritt im Protokoll. */
  private rechnen(op: Op): void {
    if (this.busy || this.zustand.register === null) return;
    if (this.takte.length >= MAX_TAKTE) {
      this.note = `Höchstens ${MAX_TAKTE} Takte -- mit „Neu“ fangen Sie von vorn an.`;
      this.render();
      return;
    }
    const step = takt(this.task, this.zustand, { op });
    if (!step) return;
    const [neu, ev] = step;
    this.takte.push({ op });
    store.saveTakte(this.task.id, this.takte);
    this.zustand = neu;
    this.ereignisse.push(ev);
    this.busWert = null;
    this.result = null;
    this.flash.add("register");
    this.note = beschreibe(ev);
    this.evaluate(false);
  }

  private action(act: string): void {
    if (this.busy) return;
    switch (act) {
      case "takt": void this.runTakt(); return;
      case "undo":
        if (!this.takte.length) return;
        this.takte.pop();
        this.afterChange();
        return;
      case "clear":
        if (!this.takte.length) return;
        this.takte = [];
        this.afterChange();
        return;
      case "hint":
        if (this.hintsShown < this.task.hinweise.length) {
          this.hintsShown++;
          store.saveHints(this.task.id, this.hintsShown);
          this.render();
        }
        return;
      case "check":
        this.evaluate(true);
        return;
    }
  }

  private afterChange(): void {
    store.saveTakte(this.task.id, this.takte);
    this.replay();
    this.busWert = null;
    this.note = "";
    this.result = null;
    this.solvedShown = false;
    this.render();
  }

  // ---------------------------------------------------------------- Takt mit Animation

  private async runTakt(): Promise<void> {
    if (this.takte.length >= MAX_TAKTE) {
      this.note = `Höchstens ${MAX_TAKTE} Takte -- mit „Neu“ fangen Sie von vorn an.`;
      this.render();
      return;
    }
    const sig = this.signal();
    if (this.task.daten_aus_register && sig.RW === 0 && (sig.CS_S || sig.CS_E) && sig.D !== this.zustand.register) {
      this.note = this.zustand.register === null
        ? "Im Register steht noch nichts. Lesen Sie zuerst eine Speicherzelle."
        : "Die Datenleitungen zeigen nicht, was im Register steht. Tippen Sie erst auf „Register → Datenbus“.";
      this.render();
      return;
    }
    const step = takt(this.task, this.zustand, sig);
    if (!step) return;
    const [neu, ev] = step;
    this.busy = true;
    this.note = "";
    this.result = null;
    this.busWert = null;
    this.render();

    if (!reducedMotion()) {
      await this.fly(this.cpuFlights(sig));
      if (!this.root.isConnected) return;
      if (ev.typ === "lesen") {
        this.flash.add(`row:${parseInt(ev.adresse, 2)}`);
        this.render();
        await wait(250);
        await this.fly(this.readFlights(ev.wert));
        if (!this.root.isConnected) return;
      }
    }

    this.takte.push(sig);
    store.saveTakte(this.task.id, this.takte);
    this.zustand = neu;
    this.ereignisse.push(ev);
    if (ev.typ === "lesen") {
      this.busWert = ev.wert;
      this.flash.add("register");
    } else if (ev.typ === "schreiben") {
      if (ev.ziele.includes("speicher")) this.flash.add(`row:${parseInt(ev.adresse, 2)}`);
      if (ev.ziele.includes("ea")) this.flash.add("ea");
    }
    this.note = beschreibe(ev);
    this.busy = false;
    this.evaluate(false);
  }

  /** Bits, die die CPU anlegt: Adresse und Steuerleitungen immer, Daten nur beim Schreiben. */
  private cpuFlights(s: Signal): Flight[] {
    const out: Flight[] = [];
    const bits: [string, string][] = [
      ...this.a.map((b, i) => [`A${i}`, String(b)] as [string, string]),
      ["RW", String(s.RW)], ["CS_S", String(s.CS_S ?? 0)],
    ];
    if (this.task.ea) bits.push(["CS_E", String(s.CS_E ?? 0)]);
    if (s.RW === 0) this.d.forEach((b, i) => bits.push([`D${i}`, String(b)]));
    for (const [key, bit] of bits) for (const pts of this.paths(key)) out.push({ pts, bit });
    return out;
  }

  /** Beim Lesen treibt der Speicher den Datenbus zurück zur CPU, bis ins Datenregister. */
  private readFlights(wert: string): Flight[] {
    return [...wert].map((bit, i) => {
      const y = this.line(`D${i}`).y;
      return { pts: [[X_MEM, y], [X_CPU, y], [X_REG + REG_W / 2, y]], bit };
    });
  }

  /** Alle Wege einer Leitung von der CPU aus (Hauptleitung und Abzweig zum E/A-Werk). */
  private paths(key: string): [number, number][][] {
    const { y } = this.line(key);
    if (key === "CS_E") {
      const x = this.xBranch(key)!;
      return [[[X_CPU, y], [x, y], [x, this.eaTop]]];
    }
    const main: [number, number][] = [[X_CPU, y], [X_MEM, y]];
    const bx = this.xBranch(key);
    return bx === null ? [main] : [main, [[X_CPU, y], [bx, y], [bx, this.eaTop]]];
  }

  /** x-Position, an der eine Leitung zum E/A-Werk abzweigt (null = kein Abzweig). */
  private xBranch(key: string): number | null {
    if (!this.task.ea) return null;
    if (key[0] === "D") return EA_X0 + 22 + Number(key.slice(1)) * Math.min(24, 150 / this.task.bits);
    if (key === "RW") return EA_X1 - 48;
    if (key === "CS_E") return EA_X1 - 22;
    return null;
  }

  private fly(flights: Flight[]): Promise<void> {
    const ns = "http://www.w3.org/2000/svg";
    const items = flights.map(f => {
      const g = document.createElementNS(ns, "g");
      g.setAttribute("class", `bit ${cls(f.bit)}`);
      g.innerHTML = `<circle r="10"/><text dy="4.5">${f.bit}</text>`;
      this.flyLayer.appendChild(g);
      const segs = f.pts.slice(1).map((p, i) => Math.hypot(p[0] - f.pts[i][0], p[1] - f.pts[i][1]));
      return { g, f, segs, len: segs.reduce((s, x) => s + x, 0) };
    });
    // Alle Bits brauchen gleich lang für ihre Hauptstrecke; Abzweige dürfen länger sein
    const ref = Math.max(...items.map(it => Math.abs(it.f.pts[1][0] - it.f.pts[0][0])), 1);
    const total = FLY_MS * Math.max(...items.map(it => it.len)) / ref;
    return new Promise(resolve => {
      const t0 = performance.now();
      const frame = (now: number) => {
        const dist = ((now - t0) / FLY_MS) * ref;
        for (const it of items) {
          let d = Math.min(dist, it.len), k = 0;
          while (k < it.segs.length - 1 && d > it.segs[k]) { d -= it.segs[k]; k++; }
          const [x0, y0] = it.f.pts[k], [x1, y1] = it.f.pts[k + 1];
          const t = it.segs[k] ? Math.min(1, d / it.segs[k]) : 1;
          it.g.setAttribute("transform", `translate(${x0 + (x1 - x0) * t},${y0 + (y1 - y0) * t})`);
        }
        if (now - t0 < total && this.root.isConnected) requestAnimationFrame(frame);
        else {
          items.forEach(it => it.g.remove());
          resolve();
        }
      };
      requestAnimationFrame(frame);
    });
  }

  // ---------------------------------------------------------------- Prüfen

  private stars(r: ZyklusResult): number {
    return 1 + (r.par_erreicht ? 1 : 0) + (r.par_erreicht && this.hintsShown === 0 ? 1 : 0);
  }

  /** Nach jedem Takt still prüfen (Erfolg sofort feiern), mit „Prüfen“ auch Fehler zeigen. */
  private evaluate(explicit: boolean): void {
    const r = checkZyklus(this.task, { takte: this.takte });
    if (r.solved && !this.solvedShown) {
      this.solvedShown = true;
      this.result = r;
      const stars = this.stars(r);
      const before = store.stars();
      store.saveStars(this.task.id, stars);
      this.root.dispatchEvent(new CustomEvent("checked", { detail: { loesung: { takte: this.takte }, hints: this.hintsShown } }));
      this.root.dispatchEvent(new CustomEvent("solved", { detail: { stars, before } }));
      this.popStars = true;
    } else if (explicit) {
      this.result = r;
      this.root.dispatchEvent(new CustomEvent("checked", { detail: { loesung: { takte: this.takte }, hints: this.hintsShown } }));
    }
    this.render();
    this.popStars = false;
  }

  // ---------------------------------------------------------------- Darstellung

  private render(): void {
    this.svg.querySelector("g.layer")!.innerHTML = this.drawing();
    this.flash.clear();
    this.renderToolbar();
    this.renderSide();
  }

  private renderToolbar(): void {
    const q = (act: string) => this.root.querySelector<HTMLButtonElement>(`[data-act="${act}"]`)!;
    for (const act of ["takt", "undo", "clear", "check"]) q(act).disabled = this.busy;
    q("undo").disabled ||= this.takte.length === 0;
    q("clear").disabled ||= this.takte.length === 0;
    const hint = q("hint");
    const left = this.task.hinweise.length - this.hintsShown;
    hint.innerHTML = `${ICON.bulb}<span>Hinweis${left > 0 ? ` (${left})` : ""}</span>`;
    hint.disabled = left <= 0;
    hint.hidden = this.task.hinweise.length === 0;
  }

  /** Aktueller Pegel einer Leitung: "hi", "lo" oder "unk" (Datenbus beim Lesen hochohmig). */
  private level(key: string): string {
    const i = Number(key.slice(1));
    if (key === "RW") return cls(this.rw);
    if (key === "CS_S") return cls(this.csS);
    if (key === "CS_E") return cls(this.csE);
    if (key[0] === "A") return cls(this.a[i]);
    if (this.rw === 0) return cls(this.d[i]);
    return this.busWert ? cls(this.busWert[i]) : "unk";
  }

  private drawing(): string {
    const t = this.task, z = this.zustand;
    const parts: string[] = [];
    const cpuBottom = this.H - 20;

    // Gruppenbeschriftung auf dem Bus
    const groups: [Gruppe, string][] = [["A", "Adressbus"], ["D", "Datenbus"], ["S", "Steuerbus"]];
    for (const [g, name] of groups) {
      const first = this.lines.find(l => l.gruppe === g)!;
      parts.push(`<text class="bus-label" x="${(X_CPU + X_MEM) / 2 - 120}" y="${first.y - 16}">${name}</text>`);
    }

    // Leitungen
    for (const l of this.lines) {
      const lv = this.level(l.key);
      for (const pts of this.paths(l.key)) {
        parts.push(`<path class="wire ${lv}${l.gruppe === "D" ? " data" : ""}" d="M${pts.map(p => p.join(" ")).join(" L")}"/>`);
      }
      const bx = this.xBranch(l.key);
      if (bx !== null && l.key !== "CS_E") parts.push(`<circle class="dot ${lv}" cx="${bx}" cy="${l.y}" r="5"/>`);
    }

    // CPU mit Schaltern und Register
    parts.push(`<rect class="box" x="20" y="20" width="${X_CPU - 20}" height="${cpuBottom - 20}" rx="14"/>
      <text class="box-title" x="${(20 + X_CPU) / 2}" y="52">CPU</text>`);
    for (const l of this.lines) {
      const off = l.gruppe === "D" && this.rw === 1;
      const lv = off ? "off" : this.level(l.key);
      const val = off ? "–" : lv === "hi" ? "1" : "0";
      // D: Register und Knopf liegen links vom Schalter, die Trefferfläche darf sie nicht überdecken
      const hitX = l.gruppe === "D" ? X_REG_BTN + REG_BTN_W + 2 : X_TOGGLE - 150;
      const locked = l.gruppe === "D" && !!t.daten_aus_register;
      const lock = locked ? `<g class="lock" transform="translate(${X_TOGGLE + 50} ${l.y - 12})">
          <circle r="8"/><path d="M-2.5 -0.5V-2.5A2.5 2.5 0 0 1 2.5 -2.5V-0.5"/><rect x="-3.5" y="-0.5" width="7" height="5" rx="1"/></g>` : "";
      parts.push(`<text class="line-name" x="40" y="${l.y + 5}">${l.key === "RW" ? RW_SVG : l.name}</text>
        <g class="toggle ${lv}${locked ? " locked" : ""}" data-line="${l.key}" role="button"
           aria-label="${l.name} = ${val}${locked ? " (gesperrt)" : ""}"${locked ? ` aria-disabled="true"` : ""}>
          <rect class="toggle-hit" x="${hitX}" y="${l.y - 15}" width="${X_CPU - hitX}" height="30"/>
          <rect x="${X_TOGGLE}" y="${l.y - 13}" width="52" height="26" rx="7"/>
          <text x="${X_TOGGLE + 26}" y="${l.y + 6}">${val}</text>
          ${lock}
        </g>`);
      if (l.key === "RW") {
        parts.push(`<text class="wire-note" x="${X_CPU + 10}" y="${l.y - 7}">${this.rw ? "lesen" : "schreiben"}</text>`);
      }
    }
    if (this.rw === 1) {
      const dLast = this.line(`D${this.task.bits - 1}`).y;
      // an der CPU-Kante, links der Abzweige zum E/A-Werk (sonst kreuzt der Text die Leitungen)
      parts.push(`<text class="wire-note read-note" x="${X_CPU + 10}" y="${dLast + 22}">← Speicher legt die Daten an</text>`);
    }
    // Datenregister direkt an den Datenleitungen: Ziel beim Lesen, Quelle beim Schreiben
    const d0 = this.line("D0").y, dN = this.line(`D${t.bits - 1}`).y;
    const reg = z.register ?? "".padStart(t.bits, " ");
    parts.push(`<text class="reg-title" x="${X_REG - 4}" y="${d0 - 22}">Register</text>
      <g class="cells reg ${this.flash.has("register") ? "flash" : ""}">${[...reg].map((b, i) => {
        const y = this.line(`D${i}`).y - 13;
        return `<rect class="cell ${b === "1" ? "hi" : b === "0" ? "lo" : "unk"}" x="${X_REG}" y="${y}" width="${REG_W}" height="26" rx="4"/>
          <text class="cell-val" x="${X_REG + REG_W / 2}" y="${y + 18}">${b.trim()}</text>`;
      }).join("")}</g>`);
    // Zwei Quellen für Schreibdaten: D-Schalter = feste Zahl aus dem Befehl, Knopf = Wert aus dem Register
    const btnOff = z.register === null || this.busy;
    const bx = X_REG_BTN + REG_BTN_W / 2, by = (d0 + dN) / 2;
    const label = dN - d0 >= 60
      ? `<text class="small" x="${bx}" y="${by - 22}">Register</text>
         <text class="arrow" x="${bx}" y="${by + 8}">→</text>
         <text class="small" x="${bx}" y="${by + 32}">Datenbus</text>`
      : `<text class="small" x="${bx}" y="${by + 4}">Reg. → D</text>`; // zu wenige Datenleitungen für drei Zeilen
    parts.push(`<g class="reg-btn${btnOff ? " disabled" : ""}" data-act="reg-to-bus" role="button"
        aria-label="Register auf den Datenbus legen" aria-disabled="${btnOff}">
      <title>Registerinhalt an die Datenleitungen legen (zum Schreiben)</title>
      <rect x="${X_REG_BTN}" y="${d0 - 13}" width="${REG_BTN_W}" height="${dN - d0 + 26}" rx="8"/>
      ${label}
    </g>`);
    // Rechnen im Register: zwei kleine Knöpfe direkt darunter
    (["inc", "shl"] as Op[]).forEach((op, k) => {
      const x = 40 + k * (OP_W + 8), y = dN + 19; // endet vor den Schaltern (X_TOGGLE)
      const title = op === "inc" ? "Register + 1" : "Register × 2 (alle Bits eine Stelle nach links)";
      parts.push(`<g class="reg-btn op${btnOff ? " disabled" : ""}" data-act="${op}" role="button"
          aria-label="${title}" aria-disabled="${btnOff}">
        <title>${title}</title>
        <rect x="${x}" y="${y}" width="${OP_W}" height="${OP_H}" rx="6"/>
        <text x="${x + OP_W / 2}" y="${y + 17}">${OP_TEXT[op]}</text>
      </g>`);
    });

    // „Takt“ auch direkt unter den Schaltern (der Knopf in der Werkzeugleiste ist weit unten)
    const tx = X_CPU - 16 - TAKT_W, ty = this.lines[this.lines.length - 1].y + 32;
    parts.push(`<g class="takt-btn${this.busy ? " disabled" : ""}" data-act="takt" role="button"
        aria-label="Takt" aria-disabled="${this.busy}">
      <title>Takt: die eingestellten Bits über den Bus schicken</title>
      <rect x="${tx}" y="${ty}" width="${TAKT_W}" height="${TAKT_H}" rx="8"/>
      <path d="M${tx + 22} ${ty + 10} l11 7 -11 7z"/>
      <text x="${tx + 62}" y="${ty + 23}">Takt</text>
    </g>`);

    // Speicher als Fächerregal (ein Fach je Speicherzelle)
    const mx0 = X_MEM, mx1 = W - 12, top = this.memTop, bottom = this.memBottom;
    const head = 52; // Platz für Titel und Spaltenköpfe über dem ersten Fach
    const rh = (bottom - top - head) / t.woerter;
    const cell = Math.min(22, 120 / t.bits);
    // Adresse direkt vor den Inhalt („101 (5): 1011“), zusammen mittig im Fach; Breiten geschätzt
    const fx0 = mx0 + 58, fw = mx1 - mx0 - 66;
    const labelW = t.abits * 9.2 + 6 + (String(t.woerter - 1).length + 2) * 7.5 + 6;
    const labelEnd = fx0 + (fw - (labelW + 8 + t.bits * cell)) / 2 + labelW;
    const cellsX = labelEnd + 8;
    parts.push(`<rect class="box" x="${mx0}" y="${top}" width="${mx1 - mx0}" height="${bottom - top}" rx="14"/>
      <text class="box-title" x="${(mx0 + 60 + mx1) / 2}" y="${top + 24}">Speicher</text>
      <text class="fach-head end" x="${labelEnd}" y="${top + head - 6}">Adresse</text>
      <text class="fach-head" x="${cellsX}" y="${top + head - 6}">Inhalt</text>`);
    for (const l of this.lines) {
      if (l.key === "CS_E") continue;
      parts.push(`<text class="pin-name" x="${mx0 + 6}" y="${l.y + 4}">${l.key === "RW" ? RW_SVG : l.name.replace("CS Speicher", "CS")}</text>`);
    }
    const sel = this.csS ? parseInt(this.a.join(""), 2) : -1;
    for (let i = 0; i < t.woerter; i++) {
      const y = top + head + i * rh;
      const adr = i.toString(2).padStart(t.abits, "0");
      const changed = z.speicher[i] !== t.inhalt[i];
      const c = ["fach", i === sel ? "sel" : "", changed ? "changed" : "", this.flash.has(`row:${i}`) ? "flash" : ""].join(" ");
      parts.push(`<g class="${c}">
        <rect x="${mx0 + 58}" y="${y + 1}" width="${mx1 - mx0 - 66}" height="${rh - 2}" rx="5"/>
        <text class="fach-label" x="${labelEnd}" y="${y + rh / 2 + 5}"><tspan class="fach-adr">${adr}</tspan> <tspan class="fach-dez">(${i})</tspan><tspan class="fach-adr">:</tspan></text>
        ${this.cells(z.speicher[i], t.bits, cellsX, y + (rh - Math.min(rh - 6, 20)) / 2, cell, "", Math.min(rh - 6, 20))}
      </g>`);
    }

    // E/A-Werk
    if (t.ea) {
      const flash = this.flash.has("ea") ? " flash" : "";
      const lamps = [...(z.ea ?? "".padStart(t.bits, "-"))].map((b, i) => {
        const x = this.xBranch(`D${i}`)!;
        return `<circle class="lamp ${b === "1" ? "hi" : b === "0" ? "lo" : "unk"}" cx="${x}" cy="${this.eaTop + 34}" r="9"/>`;
      }).join("");
      parts.push(`<g class="ea${flash}">
        <rect class="box" x="${EA_X0}" y="${this.eaTop}" width="${EA_X1 - EA_X0}" height="90" rx="14"/>
        ${this.csE ? `<rect class="ea-sel" x="${EA_X0 + 5}" y="${this.eaTop + 5}" width="${EA_X1 - EA_X0 - 10}" height="80" rx="10"/>` : ""}
        ${lamps}
        <text class="ea-title" x="${EA_X0 + 16}" y="${this.eaTop + 76}">E/A-Werk (Ausgabe)</text>
        <text class="ea-value" x="${EA_X1 - 16}" y="${this.eaTop + 76}">${z.ea === null ? "" : "= " + parseInt(z.ea, 2)}</text>
      </g>`);
    }
    return parts.join("");
  }

  private cells(value: string | null, n: number, x: number, y: number, w: number, extra: string, h = 26): string {
    const v = value ?? "".padStart(n, " ");
    return `<g class="cells ${extra}">${[...v].map((b, i) => `
      <rect class="cell ${b === "1" ? "hi" : b === "0" ? "lo" : "unk"}" x="${x + i * w}" y="${y}" width="${w - 2}" height="${h}" rx="4"/>
      <text class="cell-val" x="${x + i * w + (w - 2) / 2}" y="${y + h / 2 + 5}">${b.trim()}</text>`).join("")}</g>`;
  }

  private zielText(): string {
    const z = this.zustand;
    return this.task.ziel.map(g => {
      let text: string, ok: boolean;
      if (g.art === "speicher") {
        text = `Speicherzelle ${parseInt(g.adresse, 2)} enthält ${g.wert}`;
        ok = z.speicher[parseInt(g.adresse, 2)] === g.wert;
      } else if (g.art === "register") {
        text = `Register enthält ${g.wert}`;
        ok = z.register === g.wert;
      } else {
        text = `Ausgabe zeigt ${g.wert}`;
        ok = z.ea === g.wert;
      }
      return `<li class="${ok ? "ok" : ""}"><span class="mark">${ok ? "✓" : "○"}</span>${esc(text)}</li>`;
    }).join("");
  }

  private renderSide(): void {
    const t = this.task, r = this.result;
    let feedback = "";
    if (r?.solved) {
      const stars = this.stars(r);
      const onward = this.next
        ? `<a class="next-task" href="#${this.next.id}">Nächste Aufgabe: ${esc(this.next.titel)} ${ICON.arrow}</a>`
        : `<a class="next-task" href="#">Zur Übersicht ${ICON.arrow}</a>`;
      feedback = `<div class="feedback success">
        <div class="stars${this.popStars ? " pop" : ""}">${"★".repeat(stars)}${"☆".repeat(3 - stars)}</div>
        <strong>Geschafft!</strong> In ${r.takte} Takt${r.takte === 1 ? "" : "en"}.
        ${!r.par_erreicht ? `<br>Es geht auch mit ${t.par} Takt${t.par === 1 ? "" : "en"} -- schaffen Sie das?` : ""}
        ${r.par_erreicht && this.hintsShown > 0 ? "<br>Den dritten Stern gibt es, wenn Sie es ohne Hinweis schaffen." : ""}
        ${onward}
      </div>`;
    } else if (r) {
      feedback = `<div class="feedback error"><strong>Noch nicht ganz.</strong>
        <ul>${r.problems.map(p => `<li>${esc(p.message)}</li>`).join("")}</ul></div>`;
    }

    const cols = t.ea ? 5 : 4;
    const rows = this.takte.map((s, i) => `<tr>
        <td>${i + 1}</td>${istRechnen(s)
          ? `<td colspan="${cols}" class="mono">Register ${OP_TEXT[s.op]}</td>`
          : `<td class="mono">${s.A}</td><td class="mono">${s.RW === 0 ? s.D : "–"}</td>
             <td>${s.RW}</td><td>${s.CS_S ?? 0}</td>${t.ea ? `<td>${s.CS_E ?? 0}</td>` : ""}`}
      </tr><tr class="event"><td></td><td colspan="${t.ea ? 5 : 4}">${esc(beschreibe(this.ereignisse[i]))}</td></tr>`).join("");
    const hints = t.hinweise.slice(0, this.hintsShown).map(h => `<li>${rwHtml(esc(h))}</li>`).join("");

    this.side.innerHTML = `
      ${feedback}
      <div class="ziel"><strong>Ziel</strong><ul>${this.zielText()}</ul></div>
      ${this.note && !r ? `<p class="note" role="status">${rwHtml(esc(this.note))}</p>` : ""}
      <table class="protokoll">
        <thead><tr><th>#</th><th>A</th><th>D</th><th>${RW_HTML}</th><th>CS<sub>S</sub></th>${t.ea ? "<th>CS<sub>E</sub></th>" : ""}</tr></thead>
        <tbody>${rows || `<tr class="empty"><td colspan="${t.ea ? 6 : 5}">Noch kein Takt</td></tr>`}</tbody>
      </table>
      <p class="tip">Tippen Sie auf die Schalter in der CPU, um Leitungen auf 0 oder 1 zu setzen,
        dann auf „Takt“. ${RW_HTML} = 1 heißt lesen, ${RW_HTML} = 0 schreiben.</p>
      ${t.daten_aus_register
        ? `<p class="tip">In dieser Aufgabe sind die Schalter D${t.bits - 1}–D0 gesperrt (Schloss):
            Geschrieben wird nur, was im Register liegt. Also erst lesen, dann „Register → Datenbus“.</p>`
        : `<p class="tip">Zum Schreiben gibt es zwei Quellen: Die Schalter D${t.bits - 1}–D0 sind eine feste Zahl, wie
            sie direkt im Befehl steht. „Register → Datenbus“ legt stattdessen den Wert an, der im
            Register liegt, z. B. nach einem Lesezugriff.</p>`}
      <p class="tip">Rechnen kann die CPU nur im Register: „+1“ erhöht den Wert um eins, „×2“ verdoppelt
        ihn, indem alle Bits eine Stelle nach links rücken. Jede Rechnung braucht einen Takt.</p>
      ${hints ? `<div class="hints"><strong>Hinweise</strong><ul>${hints}</ul></div>` : ""}`;
  }
}
